import { useEffect, useRef, useState } from 'react';
import type { AdminTheme, BrandingSetting } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { mediaUrl } from '@/lib/media';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { UploadIcon } from '@/components/icons';
import { Page, Section } from '@/components/page';
import { Alert, Button, Field, Input, Select, Skeleton, cx } from '@/components/ui';

const FONTS = ['system', 'cairo', 'tajawal', 'ibm-plex'] as const;
const RADII = ['sharp', 'soft', 'round'] as const;

/**
 * The store's own look (ADR-026).
 *
 * This is what makes the platform white-label rather than "our shop with your
 * products in it": colours, logo, type and corner softness, changed here and
 * live on the storefront without a deployment.
 *
 * **One colour, ten shades.** Nobody hands over a full ramp — a brand guideline
 * has one green on it — so the API derives the scale and this screen shows what
 * it produced. Typing ten shades by hand is how a store ends up with a hover
 * state lighter than the button it is hovering.
 *
 * The settings in the wide column, the preview in the side column beside them,
 * so every change is seen next to what it changed.
 */
/** What the API takes as a brand colour: `#` and six hex digits. */
const HEX = /^#[0-9a-fA-F]{6}$/;

export function BrandingPage() {
  const t = useT();

  const [branding, setBranding] = useState<BrandingSetting | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [color, setColor] = useState('#0f9d77');
  const reloadWorkspace = useWorkspaceStore((state) => state.loadWorkspace);
  const logoInput = useRef<HTMLInputElement>(null);
  const faviconInput = useRef<HTMLInputElement>(null);

  const apply = (next: BrandingSetting) => {
    setBranding(next);
    setColor(next.brandColor);
  };

  useEffect(() => {
    void withSession(() => adminApi.branding.get())
      .then(apply)
      .catch((cause) => setError(errorMessage(cause, 'Could not load the branding.')))
      .finally(() => setLoading(false));
  }, []);

  const save = async (patch: Parameters<typeof adminApi.branding.update>[0]) => {
    setSaving(true);
    setError(null);

    try {
      apply(await withSession(() => adminApi.branding.update(patch)));
      // The shell wears what was just chosen — its colour, its logo — without a reload.
      void reloadWorkspace();
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save the branding.'));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File, field: 'logoKey' | 'faviconKey') => {
    setSaving(true);

    try {
      const result = await withSession(() => adminApi.uploads.image(file));
      await save({ [field]: result.key });
    } catch (cause) {
      setError(errorMessage(cause, 'Could not upload the image.'));
      setSaving(false);
    }
  };

  if (loading || !branding) {
    return (
      <Page title={t('branding')} subtitle={t('brandingSubtitle')} width="wide">
        {loading ? <Skeleton className="h-64" /> : error ? <Alert>{error}</Alert> : null}
      </Page>
    );
  }

  return (
    <Page title={t('branding')} subtitle={t('brandingSubtitle')} width="wide">
      {error ? <Alert>{error}</Alert> : null}

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="flex min-w-0 flex-col gap-5">
          <Section title={t('brandColor')} subtitle={t('brandColorHint')}>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t('pickAColor')}</span>
                <input
                  type="color"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                  className="h-10 w-20 cursor-pointer rounded-control border border-line bg-surface p-1"
                />
              </label>

              <Field
                label={t('hexValue')}
                htmlFor="brand-hex"
                error={HEX.test(color) ? undefined : t('hexInvalid')}
              >
                <Input
                  id="brand-hex"
                  dir="ltr"
                  value={color}
                  invalid={!HEX.test(color)}
                  onChange={(event) => setColor(event.target.value.trim())}
                  className="w-40"
                />
              </Field>

              <Button
                disabled={saving || color === branding.brandColor || !HEX.test(color)}
                onClick={() => void save({ brandColor: color })}
              >
                {saving ? t('saving') : t('save')}
              </Button>
            </div>

            {/* What the store actually got. Ten swatches beat a promise. */}
            <div className="mt-5 flex flex-wrap gap-1">
              {Object.entries(branding.tokens)
                .filter(([name]) => name.startsWith('--color-brand-'))
                .map(([name, value]) => (
                  <span
                    key={name}
                    title={`${name.replace('--color-brand-', '')} · ${value}`}
                    style={{ background: value }}
                    className="size-10 rounded-control border border-line"
                  />
                ))}
            </div>
          </Section>

          <Section title={t('logoAndFavicon')} subtitle={t('logoHint')}>
            <div className="grid gap-5 sm:grid-cols-2">
              <ImageSlot
                label={t('storeLogo')}
                url={branding.logoUrl}
                onPick={() => logoInput.current?.click()}
                onClear={() => void save({ logoKey: null })}
                saving={saving}
                clearLabel={t('removeImage')}
                pickLabel={t('uploadImage')}
                emptyLabel={t('noLogoYet')}
              />
              <ImageSlot
                label={t('favicon')}
                url={branding.faviconUrl}
                onPick={() => faviconInput.current?.click()}
                onClear={() => void save({ faviconKey: null })}
                saving={saving}
                clearLabel={t('removeImage')}
                pickLabel={t('uploadImage')}
                emptyLabel={t('noFaviconYet')}
              />
            </div>

            <input
              ref={logoInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file, 'logoKey');
                event.target.value = '';
              }}
            />
            <input
              ref={faviconInput}
              type="file"
              accept="image/png,image/x-icon,image/svg+xml"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file, 'faviconKey');
                event.target.value = '';
              }}
            />
          </Section>

          {/* What the back office wears (ADR-123): ours, or the store's colour. */}
          <Section title={t('adminColors')} subtitle={t('adminColorsHint')}>
            <div role="radiogroup" aria-label={t('adminColors')} className="grid gap-3 sm:grid-cols-2">
              {ADMIN_OPTIONS.map((option) => {
                const checked = branding.adminTheme === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    disabled={saving}
                    onClick={() => (checked ? undefined : void save({ adminTheme: option.id }))}
                    className={cx(
                      'flex flex-col gap-3 rounded-card border p-3 text-start transition-[border-color,box-shadow] disabled:cursor-wait',
                      checked ? 'border-brand-500 ring-3 ring-brand-500/15' : 'border-line hover:border-line-strong',
                    )}
                  >
                    <AdminMiniature colors={option.id === 'platform' ? PLATFORM_COLORS : storeColors(branding)} />
                    <span className="flex items-start gap-2.5 px-1 pb-1">
                      <span
                        aria-hidden
                        className={cx(
                          'mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full border-2',
                          checked ? 'border-brand-600' : 'border-line-strong',
                        )}
                      >
                        {checked ? <span className="size-2 rounded-full bg-brand-600" /> : null}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink">{t(option.label)}</span>
                        <span className="mt-0.5 block text-xs text-ink-muted">{t(option.hint)}</span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {branding.adminTheme === 'store' && !branding.tokens['--color-brand-600'] ? (
              <div className="mt-4">
                <Alert tone="info">{t('adminThemeNeedsColor')}</Alert>
              </div>
            ) : null}
          </Section>

          <Section title={t('typeAndShape')} subtitle={t('typeAndShapeHint')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('fontFamily')} htmlFor="brand-font">
                <Select
                  id="brand-font"
                  value={branding.fontFamily}
                  disabled={saving}
                  onChange={(event) => void save({ fontFamily: event.target.value })}
                >
                  {FONTS.map((font) => (
                    <option key={font} value={font}>
                      {t(`font${font.replace('-', '')}` as MessageKey)}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label={t('cornerStyle')} htmlFor="brand-radius">
                <Select
                  id="brand-radius"
                  value={branding.radius}
                  disabled={saving}
                  onChange={(event) => void save({ radius: event.target.value })}
                >
                  {RADII.map((radius) => (
                    <option key={radius} value={radius}>
                      {t(`radius${radius}` as MessageKey)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Section>
        </div>

        {/* A preview built from the same tokens the storefront gets, so what is
            shown here is what a shopper sees — not an approximation of it. It
            stays in view while the settings beside it scroll. */}
        <Section title={`${t('brandingPreview')} · ${t('storefrontPreview')}`} subtitle={t('previewHint')} className="lg:sticky lg:top-5">
          <div
            style={branding.tokens as React.CSSProperties}
            className="flex flex-col gap-3 rounded-card border border-line p-4"
          >
            <div className="flex items-center gap-2">
              {branding.logoUrl ? (
                <img src={mediaUrl(branding.logoUrl)} alt="" className="h-6 w-auto" />
              ) : (
                <span className="text-sm font-semibold text-ink">{t('appName')}</span>
              )}
            </div>

            <div
              style={{ background: branding.tokens['--color-brand-600'] }}
              className="rounded-control px-4 py-2 text-center text-sm font-medium text-white"
            >
              {t('addToCartPreview')}
            </div>

            <div
              style={{
                background: branding.tokens['--color-brand-50'],
                color: branding.tokens['--color-brand-700'],
              }}
              className="rounded-control px-3 py-2 text-xs"
            >
              {t('promoBadgePreview')}
            </div>

            <div className="flex gap-2">
              <span
                style={{ borderColor: branding.tokens['--color-line'] }}
                className={cx('flex-1 rounded-control border px-3 py-2 text-center text-xs text-ink-muted')}
              >
                {t('secondaryPreview')}
              </span>
            </div>
          </div>
        </Section>
      </div>
    </Page>
  );
}

const ADMIN_OPTIONS: { id: AdminTheme; label: MessageKey; hint: MessageKey }[] = [
  { id: 'platform', label: 'adminThemePlatform', hint: 'adminThemePlatformHint' },
  { id: 'store', label: 'adminThemeStore', hint: 'adminThemeStoreHint' },
];

interface MiniatureColors {
  nav: string;
  page: string;
  button: string;
  mark: string;
}

/** The platform palette, as the brand guide writes it: Forest Ink, Mist, Deep Emerald, Mint. */
const PLATFORM_COLORS: MiniatureColors = { nav: '#0B231A', page: '#F1FBF6', button: '#047857', mark: '#34D399' };

/**
 * The admin in the store's colour, as the shell would draw it: the generated
 * ramp for the button and the mark, and the forest and mist re-tinted with the
 * ramp's hue. Before a colour is saved, the store's default stands in.
 */
function storeColors(branding: BrandingSetting): MiniatureColors {
  const tokens = branding.tokens;
  const hue = /oklch\([^ ]+ [^ ]+ ([\d.]+)\)/.exec(tokens['--color-brand-600'] ?? '')?.[1];
  return {
    nav: hue ? `oklch(0.234 0.035 ${hue})` : PLATFORM_COLORS.nav,
    page: hue ? `oklch(0.979 0.012 ${hue})` : PLATFORM_COLORS.page,
    button: tokens['--color-brand-600'] ?? branding.brandColor,
    mark: tokens['--color-brand-400'] ?? branding.brandColor,
  };
}

/** A thumbnail of the admin — menu, a page, a button — in one palette. */
function AdminMiniature({ colors }: { colors: MiniatureColors }) {
  return (
    <span aria-hidden className="flex h-24 overflow-hidden rounded-[0.75rem] border border-line" style={{ background: colors.page }}>
      <span className="flex w-1/4 flex-col gap-1.5 p-2" style={{ background: colors.nav }}>
        <span className="size-3.5 rounded-[4px] bg-white" />
        <span className="mt-1 h-1.5 w-full rounded-full" style={{ background: colors.mark }} />
        <span className="h-1.5 w-4/5 rounded-full bg-white/20" />
        <span className="h-1.5 w-3/5 rounded-full bg-white/20" />
      </span>
      <span className="flex flex-1 flex-col gap-2 p-2.5">
        <span className="h-2 w-2/5 rounded-full bg-black/15" />
        <span className="flex flex-1 gap-1.5">
          <span className="flex-1 rounded-md bg-white shadow-[0_0_0_1px_rgb(0_0_0/0.06)]" />
          <span className="flex-1 rounded-md bg-white shadow-[0_0_0_1px_rgb(0_0_0/0.06)]" />
        </span>
        <span className="h-3 w-1/3 self-end rounded-[4px]" style={{ background: colors.button }} />
      </span>
    </span>
  );
}

function ImageSlot({
  label,
  url,
  onPick,
  onClear,
  saving,
  pickLabel,
  clearLabel,
  emptyLabel,
}: {
  label: string;
  url: string | null;
  onPick: () => void;
  onClear: () => void;
  saving: boolean;
  pickLabel: string;
  clearLabel: string;
  emptyLabel: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink">{label}</span>

      <div className="flex h-24 items-center justify-center rounded-card border border-dashed border-line-strong bg-surface-sunken">
        {url ? (
          <span className="inline-flex h-16 items-center rounded-xl bg-white p-2 shadow-card">
            <img src={mediaUrl(url)} alt={label} className="max-h-12 w-auto" />
          </span>
        ) : (
          <span className="text-xs text-ink-faint">{emptyLabel}</span>
        )}
      </div>

      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={saving} onClick={onPick}>
          <UploadIcon className="size-4" />
          {pickLabel}
        </Button>
        {url ? (
          <Button variant="ghost" size="sm" disabled={saving} onClick={onClear}>
            {clearLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
